type TimerHandle = ReturnType<typeof setTimeout>;

/** Runs the leading update immediately and coalesces a burst into one trailing
 * update. Network state can keep arriving at full rate while expensive DOM or
 * canvas consumers refresh at a deliberate UI cadence. */
export class TrailingUiScheduler {
  private timer: TimerHandle | undefined;
  private lastRunAt = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly callback: () => void,
    private readonly intervalMs: number,
    private readonly clock: () => number = () => performance.now(),
  ) {}

  request(immediate = false): void {
    const now = this.clock();
    if (immediate || now - this.lastRunAt >= this.intervalMs) {
      if (this.timer !== undefined) clearTimeout(this.timer);
      this.timer = undefined;
      this.lastRunAt = now;
      this.callback();
      return;
    }
    if (this.timer !== undefined) return;
    const delay = Math.max(0, this.intervalMs - (now - this.lastRunAt));
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.lastRunAt = this.clock();
      this.callback();
    }, delay);
  }

  cancel(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
    this.lastRunAt = Number.NEGATIVE_INFINITY;
  }
}
